import React from "react";
import { motion } from "framer-motion";
import {
  Gavel,
  FileCheck2,
  Ship,
  ShieldCheck,
  Radar,
  PackageCheck,
} from "lucide-react";

const steps = [
  { icon: Gavel,         label: "CommodityBid™️",         desc: "Online procurement bidding", highlight: true },
  { icon: FileCheck2,    label: "Purchase Order",         desc: "Issued in workspace" },
  { icon: Ship,          label: "FreightIQ™️",            desc: "Smart freight routing" },
  { icon: ShieldCheck,   label: "Inspection™️",           desc: "Pre-shipment QA" },
  { icon: Radar,         label: "Live Shipment Tracking™️", desc: "Shipment visibility" },
  { icon: PackageCheck,  label: "Delivered",              desc: "Closed in workspace" },
];

export default function Integration() {
  return (
    <section
      data-testid="integration-section"
      id="integration"
      className="relative py-24 md:py-32 bg-[#0B1F3A] text-white overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[1100px] bg-[#3B82F6]/15 blur-[140px] rounded-full" />
        <div className="absolute inset-0 cb-grid-bg opacity-[0.12]" />
      </div>

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#93C5FD]">
            Workspace Integration
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-[42px] font-extrabold tracking-tight leading-[1.1]">
            One workspace. The entire<br className="hidden md:block" /> procurement journey.
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-white/70 leading-relaxed">
            After you select the winning manufacturer, CommodityBid™️ flows seamlessly into
            Purchase Order, FreightIQ™️, Inspection™️ and Live Shipment Tracking™️ —
            all inside the same workspace.
          </p>
        </motion.div>

        {/* Workflow */}
        <div className="relative mt-16">
          <svg
            className="absolute top-[42px] left-[5%] right-[5%] hidden lg:block"
            height="2"
            preserveAspectRatio="none"
            width="90%"
            aria-hidden
          >
            <line
              x1="0"
              y1="1"
              x2="100%"
              y2="1"
              stroke="rgba(147,197,253,0.35)"
              strokeWidth="2"
              strokeDasharray="6 6"
              className="cb-dash-anim"
            />
          </svg>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-2 relative z-10">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: 0.06 * i }}
                  data-testid={`workflow-step-${i}`}
                  className={`relative rounded-2xl p-4 flex flex-col items-center text-center backdrop-blur-sm transition-all duration-300 ${
                    s.highlight
                      ? "bg-[#3B82F6] border border-[#60A5FA] shadow-[0_8px_30px_rgba(59,130,246,0.35)]"
                      : "bg-white/[0.04] border border-white/10 hover:bg-white/[0.07]"
                  }`}
                >
                  <div
                    className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                      s.highlight ? "bg-white/15" : "bg-white/[0.06]"
                    }`}
                  >
                    <Icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                  </div>
                  <p className="mt-3 font-display font-semibold text-[13px] tracking-tight text-white whitespace-nowrap">
                    {s.label}
                  </p>
                  <p className="mt-0.5 text-[11px] text-white/60 leading-tight">
                    {s.desc}
                  </p>
                  <span className="mt-2 text-[10px] font-bold tracking-wider text-white/40">
                    0{i + 1}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 flex items-center justify-center gap-2 text-[13px] text-white/60"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#60A5FA] cb-pulse" />
          One Workspace · One workflow · One source of truth.
        </motion.div>
      </div>
    </section>
  );
}
